import { useState, useEffect, useRef } from 'react';
import { useForm, FormProvider } from '@xmanscript/useform';
import { PlusIcon } from '@radix-ui/react-icons';
import AddLinkDrawer from '@/ui/_lib/Drawer/AddLinkDrawer';
import DeleteLinkDrawer from '@/ui/_lib/Drawer/DeleteLinkDrawer';
import SuccessMessage from '@/ui/_lib/Drawer/SuccessMessage';
import { AuthenticatedLinkService, ProductService, UserService } from '@/ui/_apiUrls';
import Product from '@/ui/_lib/links/TemplateTab/Product';
import AddProductDrawer from '@/ui/_lib/Drawer/AddProductDrawer';
import DeleteProductDrawer from '@/ui/_lib/Drawer/DeleteProductDrawer';
import LocationDrawer from '@/ui/_lib/Drawer/LocationDrawer';
import FormControl from '@/ui/_lib/FormControl';
import Button from '@/ui/_lib/Button';
import { useUser } from '@/contexts/UserContext/UserContext';

function Template3() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editableLink, setEditableLink] = useState(null);
  const [isSuccessMessageOpen, setIsSuccessMessageOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isDeleteDrawerOpen, setIsDeleteDrawerOpen] = useState(false);
  const [selectedLinkForDelete, setSelectedLinkForDelete] = useState<Record<string, any> | null>(null);

  // Add ref to track if we've already shown the limit error
  const hasShownLinkLimitError = useRef(false);
  const hasShownHighlightLimitError = useRef(false);

  const { user } = useUser();

  // Highlight-related state
  const [highlightTitle, setHighlightTitle] = useState(user?.highlight_title || 'Highlights');
  const [editingHighlightTitle, setEditingHighlightTitle] = useState(false);
  const [isAddHighlightDrawerOpen, setIsAddHighlightDrawerOpen] = useState(false);
  const [highlightToEdit, setHighlightToEdit] = useState<Record<string, any> | null>(null);
  const [isDeleteHighlightDrawerOpen, setIsDeleteHighlightDrawerOpen] = useState(false);
  const [highlightToDelete, setHighlightToDelete] = useState<Record<string, any> | null>(null);

  // Location-related state
  const [locationTitle, setLocationTitle] = useState(user?.location_title || 'Location');
  const [editingLocationTitle, setEditingLocationTitle] = useState(false);
  const [isLocationDrawerOpen, setIsLocationDrawerOpen] = useState(false);

  // Update titles when user data changes
  useEffect(() => {
    if (user?.highlight_title) {
      setHighlightTitle(user.highlight_title);
    }
    if (user?.location_title) {
      setLocationTitle(user.location_title);
    }
  }, [user?.highlight_title, user?.location_title]);

  const { mutate: patchUserTitle } = UserService.patch({
    mutationParams: {
      onSuccess: () => {
        setSuccessMessage('Title updated successfully');
        setIsSuccessMessageOpen(true);
      },
      onError: () => {

        setSuccessMessage('Failed to update title');
        setIsSuccessMessageOpen(true);
      }
    }
  });

  // Add ref to track data fetching to prevent continuous refetching
  const hasFetchedLinks = useRef(false);
  const hasFetchedProducts = useRef(false);

  // Form for editing highlight title
  const highlightFormMethods = useForm({ 
    formName: 'highlight-title-form',
    initialValues: { highlightTitle: 'Highlights' },
    persistValues: false,
    touchOnChange: false,
    validationSchema: (props: Record<string, any>) => {
      const errors: Record<string, any> = {};
      const { highlightTitle: titleValue } = props;
      if (!titleValue?.trim()) {
        errors.highlightTitle = 'Highlight title is required.';
      }
      return errors;
    },
    submitHandler: async (vals) => {
      const { currentPacket: { highlightTitle: titleValue } } = vals;
      
      // Update backend
      patchUserTitle({ highlight_title: titleValue });
      
      setHighlightTitle(titleValue);
      setEditingHighlightTitle(false);
    },
  });

  const { register: registerHighlight, onSubmitHandler: onSubmitHighlightHandler, formState: highlightFormState, setValues: setHighlightValues } = highlightFormMethods;

  // Form for editing location title
  const locationFormMethods = useForm({ 
    formName: 'location-title-form',
    initialValues: { locationTitle: 'Location' },
    persistValues: false,
    touchOnChange: false,
    validationSchema: (props: Record<string, any>) => {
      const errors: Record<string, any> = {};
      const { locationTitle: titleValue } = props;
      if (!titleValue?.trim()) {
        errors.locationTitle = 'Location title is required.';
      }
      return errors;
    },
    submitHandler: async (vals) => {
      const { currentPacket: { locationTitle: titleValue } } = vals;
      
      // Update backend
      patchUserTitle({ location_title: titleValue });
      
      setLocationTitle(titleValue);
      setEditingLocationTitle(false);
    },
  });

  const { register: registerLocation, onSubmitHandler: onSubmitLocationHandler, formState: locationFormState, setValues: setLocationValues } = locationFormMethods;

  const { data: links } = AuthenticatedLinkService.get({ 
    queryParams: { 
      queryKey: ['link'],
      // Add refetch options to prevent continuous refetching on mobile
      refetchOnMount: hasFetchedLinks.current ? false : 'always',
      refetchOnWindowFocus: false,
      refetchInterval: false,
    } 
  });
  
  // Mark links as fetched after first load
  if (links && !hasFetchedLinks.current) {
    hasFetchedLinks.current = true;
  }

  const { data: products } = ProductService.get({ 
    queryParams: { 
      queryKey: ['products'],
      // Add refetch options to prevent continuous refetching on mobile
      refetchOnMount: hasFetchedProducts.current ? false : 'always',
      refetchOnWindowFocus: false,
      refetchInterval: false,
    } 
  });
  
  // Mark products as fetched after first load
  if (products && !hasFetchedProducts.current) {
    hasFetchedProducts.current = true;
  }

  const handleOpenDrawer = (link = null) => {
    // Check if user has reached the link limit (3 links)
    const existingLinks = links?.result || [];
    if (existingLinks.length >= 3 && !link) {
      // Only show the error message once to prevent continuous popups
      if (!hasShownLinkLimitError.current) {
        setSuccessMessage('You can only add up to 3 links.');
        setIsSuccessMessageOpen(true);
        hasShownLinkLimitError.current = true;
        // Reset the flag after a delay to allow showing the message again if needed
        setTimeout(() => {
          hasShownLinkLimitError.current = false;
        }, 5000);
      }
      return;
    }
    
    setEditableLink(link);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditableLink(null);
  };

  const handleCloseSuccessMessage = () => {
    setIsSuccessMessageOpen(false);
  };

  const handleOpenDeleteDrawer = (link: Record<string, any>) => {
    setSelectedLinkForDelete(link);
    setIsDeleteDrawerOpen(true);
    setIsDrawerOpen(false); 
  };

  const handleCloseDeleteDrawer = () => {
    setIsDeleteDrawerOpen(false);
    setSelectedLinkForDelete(null);
  };

  // Highlight-related functions
  const handleAddHighlightClick = (product?: Record<string, any>) => {
    // Check if user has reached the highlight limit (3 highlights)
    const existingProducts = products?.result || [];
    if (existingProducts.length >= 3 && !product) {
      // Only show the error message once to prevent continuous popups
      if (!hasShownHighlightLimitError.current) {
        setSuccessMessage('You can only add up to 3 highlights.');
        setIsSuccessMessageOpen(true);
        hasShownHighlightLimitError.current = true;
        // Reset the flag after a delay to allow showing the message again if needed
        setTimeout(() => {
          hasShownHighlightLimitError.current = false;
        }, 5000);
      }
      return;
    }
    
    setHighlightToEdit(product || null);
    setIsAddHighlightDrawerOpen(true);
  };

  const handleDeleteHighlightClick = (product: Record<string, any>) => {
    setHighlightToDelete(product);
    setIsDeleteHighlightDrawerOpen(true);
  };

  const handleCloseAddHighlightDrawer = () => {
    setIsAddHighlightDrawerOpen(false);
    setHighlightToEdit(null);
  };

  const handleEditLinksClick = () => {
    handleOpenDrawer();
  };

  const handleAddHighlight = () => {
    handleAddHighlightClick();
  };

  const handleLocationClick = () => {
    setIsLocationDrawerOpen(true);
  };

  // Get top 3 highlights for featured section
  const featuredHighlights = products?.result?.slice(0, 3) || [];
  const emptyHighlightSlots = Math.max(0, 3 - featuredHighlights.length);

  // Get top 3 links for link slots
  const linkSlots = links?.result?.slice(0, 3) || [];
  const emptyLinkSlots = Math.max(0, 3 - linkSlots.length);

  return (
    <FormProvider {...highlightFormMethods}>
      <div className="flex h-full w-full flex-col justify-between px-1">
        <div className="small-scrollbar flex h-full flex-col gap-3 overflow-y-auto">
          
          {/* Top Section - Featured Highlights */}
        <div className="flex flex-col gap-3 mb-4">
          {editingHighlightTitle ? (
            <form onSubmit={onSubmitHighlightHandler} className="flex flex-col gap-2">
              <FormControl
                controlType="input"
                placeholder="Highlights"
                className="w-full"
                {...registerHighlight('highlightTitle')}
              />
              <div className="flex gap-2">
                <Button
                  type="submit"
                  size="small"
                  className="text-xs"
                  disabled={highlightFormState.hasError}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  className="text-xs"
                  onClick={() => setEditingHighlightTitle(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
              <div className="flex items-center justify-between">
                <h3 className="text-[14px] font-semibold text-[#475467]">
                  {highlightTitle || 'Highlights'}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setEditingHighlightTitle(true);
                    setHighlightValues({ highlightTitle });
                  }}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Edit title"
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.7422 3.10547L11.5234 4.32422L9.02344 1.82422L10.2422 0.605469C10.3672 0.480469 10.5234 0.417969 10.7109 0.417969C10.8984 0.417969 11.0547 0.480469 11.1797 0.605469L12.7422 2.16797C12.8672 2.29297 12.9297 2.44922 12.9297 2.63672C12.9297 2.82422 12.8672 2.98047 12.7422 3.10547ZM0.929688 9.91797L8.30469 2.54297L10.8047 5.04297L3.42969 12.418H0.929688V9.91797Z" fill="#989898"/>
                  </svg>
                </button>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-4 px-1 max-[420px]:gap-1 max-[420px]:px-1">
              {featuredHighlights.map((product: any, index: number) => (
                // eslint-disable-next-line no-underscore-dangle
                <div key={product.id || product._id || `product-${index}`} className="flex flex-col items-center">
                  <Product
                    product={product}
                    onClick={() => handleAddHighlightClick(product)}
                  />
                </div>
              ))}
              {Array.from({ length: emptyHighlightSlots }).map((_, index) => (
                <div key={`empty-highlight-${index}`} className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={handleAddHighlight}
                    className="w-[109.344px] h-[140px] max-[420px]:w-full max-[420px]:h-[140px] max-[420px]:max-w-[125px] max-[420px]:mx-auto flex flex-col items-center justify-center rounded-[8.24px] bg-white border-gray-300 border-[0.51px] backdrop-blur-[10.297px] shadow-[2.57px_2.57px_5.15px_0px_rgba(0,0,0,0.25)] hover:shadow-[2.57px_2.57px_8px_0px_rgba(0,0,0,0.3)] transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#039be5]/60 focus:ring-offset-white md:shadow-none md:border-gray-200"
                  >
                    <div className="flex flex-col items-center justify-center h-full w-full">
                      <PlusIcon className="h-8 w-8 text-[#039BE5]" />
                    </div>
                  </button>
                  <div className="mt-3 text-[12px] text-[#333333] text-center w-full break-words leading-tight">
                    Click to add
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Middle Section: Location */}
          <div className="flex flex-col gap-3">
            {editingLocationTitle ? (
              <form onSubmit={onSubmitLocationHandler} className="flex flex-col gap-2">
                <FormControl
                  controlType="input"
                  placeholder="Location"
                  className="w-full"
                  {...registerLocation('locationTitle')}
                />
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    size="small"
                    className="text-xs"
                    disabled={locationFormState.hasError}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    className="text-xs"
                    onClick={() => setEditingLocationTitle(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex items-center justify-between">
                <h3 className="text-[14px] font-semibold text-[#475467]">
                  {locationTitle || 'Location'}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setEditingLocationTitle(true);
                    setLocationValues({ locationTitle });
                  }}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Edit title"
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.7422 3.10547L11.5234 4.32422L9.02344 1.82422L10.2422 0.605469C10.3672 0.480469 10.5234 0.417969 10.7109 0.417969C10.8984 0.417969 11.0547 0.480469 11.1797 0.605469L12.7422 2.16797C12.8672 2.29297 12.9297 2.44922 12.9297 2.63672C12.9297 2.82422 12.8672 2.98047 12.7422 3.10547ZM0.929688 9.91797L8.30469 2.54297L10.8047 5.04297L3.42969 12.418H0.929688V9.91797Z" fill="#989898"/>
                  </svg>
                </button>
              </div>
            )}
            <div className="flex justify-center">
              {user?.location_url ? (
                <div 
                  className="bg-white shadow-[0px_1px_2px_0px_#47546733] rounded-lg md:shadow-none md:border md:border-gray-200 relative"
                  style={{
                    width: '343px',
                    height: '166px',
                    borderRadius: '8px',
                    opacity: 1,
                    transform: 'rotate(0deg)',
                    overflow: 'hidden'
                  }}
                  onClick={handleLocationClick}
                >
                  <div className="relative w-full h-full">
                    {/* Display default map image for location */}
                    <img 
                      src="/default-map.png" 
                      alt="Location Map" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLocationClick();
                      }}
                      className="absolute top-2 right-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-1.5 shadow-sm transition-all duration-200"
                      aria-label="Edit location"
                    >
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.7422 3.10547L11.5234 4.32422L9.02344 1.82422L10.2422 0.605469C10.3672 0.480469 10.5234 0.417969 10.7109 0.417969C10.8984 0.417969 11.0547 0.480469 11.1797 0.605469L12.7422 2.16797C12.8672 2.29297 12.9297 2.44922 12.9297 2.63672C12.9297 2.82422 12.8672 2.98047 12.7422 3.10547ZM0.929688 9.91797L8.30469 2.54297L10.8047 5.04297L3.42969 12.418H0.929688V9.91797Z" fill="#989898"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  className="bg-white shadow-[0px_1px_2px_0px_#47546733] rounded-lg md:shadow-none md:border md:border-gray-200 relative flex flex-col items-center justify-center"
                  style={{
                    width: '343px',
                    height: '166px',
                    borderRadius: '8px',
                    opacity: 1,
                    transform: 'rotate(0deg)',
                    overflow: 'hidden'
                  }}
                  onClick={handleLocationClick}
                >
                  <div className="flex flex-col items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#989898"/>
                    </svg>
                    <p className="text-sm text-gray-400 mt-2">Add Map URL</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Section: Link thumbnails in flex row with edit button */}
          <div className="flex flex-row items-center justify-between gap-4 px-4 mt-1 py-2">
            <div className="flex flex-row flex-1 justify-center gap-3">
              {linkSlots.map((link: any, index: number) => (
                // eslint-disable-next-line no-underscore-dangle
                <img 
                  key={link.id || link._id || `link-${index}`}
                  src={link?.thumbnail || '/image.svg'} 
                  alt={link?.title || 'Link'} 
                  className="w-8 h-8 rounded cursor-pointer object-contain"
                  onClick={() => link && handleOpenDrawer(link)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && link) {
                      e.preventDefault();
                      handleOpenDrawer(link);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                />
              ))}
              {Array.from({ length: emptyLinkSlots }).map((_, index) => (
                <img 
                  key={`empty-link-${index}`}
                  src="/image.svg" 
                  alt="Empty Link" 
                  className="w-8 h-8 rounded cursor-pointer"
                  onClick={() => handleOpenDrawer()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpenDrawer();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={handleEditLinksClick}
              className="text-gray-500 hover:text-gray-700 cursor-pointer transition-colors"
              aria-label="Edit links"
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.7422 3.10547L11.5234 4.32422L9.02344 1.82422L10.2422 0.605469C10.3672 0.480469 10.5234 0.417969 10.7109 0.417969C10.8984 0.417969 11.0547 0.480469 11.1797 0.605469L12.7422 2.16797C12.8672 2.29297 12.9297 2.44922 12.9297 2.63672C12.9297 2.82422 12.8672 2.98047 12.7422 3.10547ZM0.929688 9.91797L8.30469 2.54297L10.8047 5.04297L3.42969 12.418H0.929688V9.91797Z" fill="#989898"/>
              </svg>
            </button>
          </div>
          

        </div>

  
      </div>

      <AddLinkDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        link={editableLink}
        onDelete={handleOpenDeleteDrawer}
      />
      <SuccessMessage
        isOpen={isSuccessMessageOpen}
        onClose={handleCloseSuccessMessage}
        message={successMessage}
      />
      <DeleteLinkDrawer
        isOpen={isDeleteDrawerOpen}
        onClose={handleCloseDeleteDrawer}
        link={selectedLinkForDelete}
        onSuccess={() => {
          setSuccessMessage('Link deleted successfully');
          setIsSuccessMessageOpen(true);
          handleCloseDeleteDrawer();
        }}
      />
      <AddProductDrawer
        isOpen={isAddHighlightDrawerOpen}
        onClose={handleCloseAddHighlightDrawer}
        product={highlightToEdit || undefined}
        onDelete={handleDeleteHighlightClick}
      />
      <DeleteProductDrawer
        isOpen={isDeleteHighlightDrawerOpen}
        onClose={() => setIsDeleteHighlightDrawerOpen(false)}
        product={highlightToDelete || null}
        onDeleteSuccess={() => {
          setSuccessMessage('Highlight deleted successfully');
          setIsSuccessMessageOpen(true);
          handleCloseAddHighlightDrawer();
          setIsDeleteHighlightDrawerOpen(false);
        }}
      />
      <LocationDrawer
        isOpen={isLocationDrawerOpen}
        onClose={() => setIsLocationDrawerOpen(false)}
        onSuccess={() => {
          setSuccessMessage('Location updated successfully');
          setIsSuccessMessageOpen(true);
        }}
      />
    </FormProvider>
  );
}

export default Template3;